/**
 * Trame pédagogique A1 / A2 — Plateau (P1 · P2 · P3).
 * Référentiel enseignant exploitable en salle, sur le plateau et en circulation.
 * Contenu 100% neutre : aucune référence à une école ou entreprise.
 *
 * Chaque fiche compétence suit la grille : objectif · pourquoi · comment ·
 * exercices · observation · risques · influences · erreurs · remédiation ·
 * questions · critère de réussite.
 */
import { CINQ_QUESTIONS, METHODE_BASELINE } from './pedagogieCommune';

export const INTRO_METHODOLOGIE = {
  baseline: METHODE_BASELINE,
  questions: CINQ_QUESTIONS,
  regleDuree: "P1, P2 et P3 ne correspondent pas à un nombre d'heures prédéfini. La durée de chaque phase est adaptée aux capacités, aux acquis et à la progression de chaque élève. Le passage à la phase suivante intervient lorsque les compétences sont suffisamment maîtrisées et reproductibles en sécurité.",
};

export const DEMI_TOUR = {
  title: 'Le demi-tour à basse vitesse',
  intro: "Le demi-tour à basse vitesse combine regard, embrayage, freinage et inertie. On l'aborde en 3 phases : préparation, prise d'angle avec débrayage, remise progressive de la motricité en sortie.",
  steps: [
    { n: 1, title: 'Je prépare mon demi-tour', bullets: ["Je réduis mon allure", "J'utilise le frein avant + le frein arrière pour obtenir l'allure adaptée", 'Je prépare mon regard vers la sortie', 'Je positionne la moto pour entrer dans le demi-tour'] },
    { n: 2, title: "J'entre dans le demi-tour", bullets: ['Je débraye complètement à la prise d\u2019angle', "La moto utilise son inertie pour poursuivre son mouvement", "Objectif : ne pas perturber la mise sur l'angle par une action brusque"] },
    { n: 3, title: 'Je sors du demi-tour', bullets: ['Je ré-embraye progressivement en remettant progressivement du gaz', 'Objectif : retrouver progressivement la motricité et stabiliser la moto en sortie'] },
  ],
  aRetenir: 'Je freine pour préparer → je débraye pour laisser vivre la moto → je regarde la sortie → je ré-embraye progressivement pour repartir.',
  disclaimer: "Cette approche est présentée comme une méthode pédagogique parmi d'autres. Chaque enseignant reste libre de l'adapter à sa pédagogie, à son élève et à sa moto.",
};

/* ================================================================== */
/*  P1 — DÉCOUVRIR ET RESSENTIR                                        */
/* ================================================================== */
const P1_EXERCICES = [
  {
    id: 'position',
    title: 'Position sur la moto',
    objectif: "L'élève trouve une position stable, mobile et compatible avec la maîtrise de la moto.",
    pourquoi: "Une position adaptée facilite l'équilibre, le contrôle des commandes, la mobilité, la précision du regard et la gestion des changements de direction.",
    comment: ['Regard loin', 'Épaules relâchées', 'Bras souples', 'Genoux en contact avec la moto', 'Pieds correctement positionnés'],
    exercices: [
      { nom: 'Position statique', niveau: 1, description: "Moto moteur coupé. L'élève s'installe, pose correctement les pieds, prend les commandes, vérifie sa position, tourne le guidon à gauche/droite et déplace son regard." },
      { nom: 'Bras souples', niveau: 2, description: "À très basse vitesse, faire vérifier : «Est-ce que tes bras tiennent la moto ou la guident simplement ?»" },
    ],
    observation: ['Bras tendus', 'Épaules relevées', 'Regard proche', 'Bassin figé', 'Genoux décollés', 'Appui excessif sur le guidon'],
    risques: ['Crispation empêchant les mouvements fins', 'Regard trop proche perturbant l’équilibre'],
    influences: ["Niveau de l'élève", 'Appréhension', 'Poids de la moto', 'Hauteur de selle', 'Expérience précédente'],
    erreurs: ['Se raidir dès que la moto avance', 'Tenir la moto avec les bras au lieu des jambes', 'Regarder les pieds ou le compteur'],
    remediation: ['Retour moteur coupé → repositionner', 'Déplacement à pied → puis très lent en patinage', 'Faire nommer chaque point d\u2019appui du corps sur la moto'],
    questions: ["«Où sont tes points d'appui sur la moto ?»", "«Est-ce que tu tiens la moto avec tes bras ou avec tes jambes ?»"],
    critere: "L'élève adopte spontanément une position équilibrée et détendue, avec un regard porté loin.",
  },
  {
    id: 'demarrage-arret',
    title: 'Démarrage et arrêt',
    objectif: "L'élève démarre sans à-coup, stabilise la moto, conserve son regard et s'arrête précisément avec les deux freins.",
    pourquoi: "Construire la coordination embrayage + accélérateur + frein + regard + équilibre, fondation de toute la conduite.",
    comment: ['Préparer sa moto', 'Engager le premier rapport', 'Rechercher le point de patinage', 'Ajouter progressivement du gaz', 'Relâcher l\u2019embrayage', 'Stabiliser son allure', 'Arrêt : fermer les gaz + 2 freins + débrayer'],
    exercices: [
      { nom: 'Départ → 10 m → arrêt', niveau: 1, description: 'Ligne droite courte. Départ, avance, arrêt précis à un repère.' },
      { nom: 'Départ → accélération légère → freinage → arrêt', niveau: 2, description: 'Ajout d\u2019une phase d\u2019accélération contrôlée.' },
      { nom: 'Départ → virage → arrêt', niveau: 3, description: 'Introduction d\u2019un changement de direction avant l\u2019arrêt.' },
    ],
    observation: ['Départ brutal', 'Perte d\u2019équilibre au démarrage', 'Regard qui plonge sur le compteur', "Arrêt long ou imprécis", 'Utilisation d\u2019un seul frein'],
    risques: ['Calée moteur', "Relâché d'embrayage trop rapide", 'Ajout de gaz brusque', 'Blocage roue à l\u2019arrêt'],
    influences: ['Type de moto', 'Charge', 'Déclivité', 'Température moteur'],
    erreurs: ['Ouvrir les gaz avant de trouver le point de patinage', 'Freiner uniquement de l\u2019arrière', 'Regarder juste devant la roue'],
    remediation: ['Isoler la recherche du point de patinage moto immobile', 'Rejouer le départ sans gaz (patinage seul)', 'Reprendre la ligne droite avant d\u2019ajouter le virage'],
    questions: ["«Qu'est-ce qui a provoqué ton départ trop brutal : embrayage, gaz ou les deux ?»", "«Où regardais-tu au moment de t'arrêter ?»"],
    critere: "L'élève enchaîne 5 départs/arrêts propres, avec les 2 freins, en conservant son équilibre et son regard.",
  },
  {
    id: 'deplacer-moteur-coupe',
    title: 'Déplacer la moto sans moteur',
    objectif: "L'élève maîtrise sa moto lorsque le moteur ne peut plus l'aider (parking, panne, manœuvre serrée).",
    pourquoi: "Compétence souvent sous-estimée mais essentielle : elle prévient la chute à l\u2019arrêt, apprend à connaître le poids et l\u2019équilibre statique de sa machine.",
    comment: ['Positionner son corps près de la moto', 'Utiliser le buste pour la sortir de béquille', 'Marcher à côté', 'Reposer proprement sur la béquille'],
    exercices: [
      { nom: 'Manipulation statique', niveau: 1, description: 'Déplacer à côté, avancer en marchant, reculer, effectuer une légère rotation, mettre sur béquille, reprendre.' },
    ],
    observation: ['Mauvais positionnement du corps', 'Moto qui bascule', 'Utilisation approximative de la béquille'],
    risques: ['Chute de la moto', 'Vouloir retenir seul une moto qui tombe (risque physique)'],
    influences: ['Poids de la moto', 'Inclinaison du sol', 'Taille du conducteur'],
    erreurs: ['Se placer trop loin de la moto', 'Béquiller moto non stabilisée', 'Forcer avec les bras au lieu du bassin'],
    remediation: ['Décomposer chaque action à l\u2019arrêt', 'Reprendre le calage sur béquille en zone plate avant de progresser'],
    questions: ["«Si la moto commence à tomber, que fais-tu ?»", "«Est-ce que tu es capable de la déplacer sans t'épuiser ?»"],
    critere: "L'élève manipule sa moto moteur coupé de manière stable, y compris en légère pente, sans compensation excessive.",
  },
  {
    id: 'patinage',
    title: 'Patinage avec et sans frein arrière',
    objectif: "L'élève construit une allure lente maîtrisée, plutôt que subie.",
    pourquoi: "Le patinage est une compétence centrale du P1. Le frein arrière devient un outil de régulation et de stabilisation à basse vitesse.",
    comment: ['Doser l\u2019embrayage dans sa zone de patinage', 'Régime moteur stable', 'Regard loin', 'Ajouter progressivement le frein arrière pour stabiliser'],
    exercices: [
      { nom: 'Recherche du point de patinage', niveau: 1, description: "Moto au ralenti. L'élève relâche progressivement l'embrayage jusqu'à ce que la moto commence à vouloir avancer, puis ré-embraye. Répéter." },
      { nom: 'Marche lente', niveau: 2, description: 'Maintenir la moto en déplacement avec embrayage dans sa zone de patinage, régime stable et regard loin.' },
      { nom: 'Patinage + frein arrière', niveau: 3, description: 'Ajouter progressivement le frein arrière pour stabiliser l\u2019allure.' },
    ],
    observation: ['Calée moteur récurrente', 'Guidonnage à basse vitesse', 'Regard trop proche', 'Absence d\u2019utilisation du frein arrière'],
    risques: ['Perte d\u2019équilibre', 'Chute à faible vitesse'],
    influences: ['Adhérence', 'Pente', 'Vent', 'Charge'],
    erreurs: ['Rechercher directement l\u2019allure lente sans construire le patinage', 'Utiliser uniquement l\u2019accélérateur pour réguler'],
    remediation: ['Isoler la recherche du point de patinage moto immobile', 'Ajouter le frein arrière seulement quand le patinage est maîtrisé'],
    questions: ["«Qu'est-ce qui te permet d'aller lentement sans caler ?»", "«Quand utilises-tu le frein arrière ?»"],
    critere: "L'élève construit une allure lente stable sur au moins 10 m, sans calée, sans poser le pied, avec un frein arrière dosé en continu.",
    aRetenir: "À basse vitesse, je ne cherche pas simplement à aller lentement : je construis une allure que je suis capable de maîtriser.",
  },
  {
    id: 'braquage',
    title: 'Braquage',
    objectif: "L'élève comprend que la moto peut tourner à faible vitesse tout en restant stable, en s'appuyant sur son regard.",
    pourquoi: "Le braquage introduit la dissociation entre trajectoire et vitesse. Le regard joue un rôle essentiel.",
    comment: ['Regarder vers la sortie de la manœuvre', 'Ne pas fixer le cône', 'Doser l\u2019embrayage', 'Utiliser le frein arrière pour stabiliser'],
    exercices: [
      { nom: 'Huit à très faible allure', niveau: 2, description: "Départ → boucle gauche → boucle droite → arrêt. Puis réduire progressivement l'espace disponible." },
    ],
    observation: ['Fixation du cône', 'Blocage guidon', 'Chute vers l\u2019intérieur', 'Trajectoire coupée'],
    risques: ['Chute intérieure', 'Perte d\u2019équilibre par blocage de direction'],
    influences: ['Regard', 'Vitesse d\u2019entrée', 'Braquage disponible'],
    erreurs: ['Regarder juste devant la roue', 'Se crisper sur le guidon', 'Bloquer volontairement la direction'],
    remediation: ['Refaire le huit très large, sans contrainte de temps', 'Placer un point de fixation visuel à la sortie', 'Reprendre la recherche du patinage'],
    questions: ["«Lorsque tu regardes le cône, que fait ta trajectoire ?»", "«À quel moment devrais-tu commencer à regarder la sortie ?»"],
    critere: "L'élève réalise le huit à faible allure sans poser le pied, avec un regard clairement porté vers la sortie de chaque boucle.",
    aRetenir: 'La moto va là où je regarde.',
  },
  {
    id: 'rapports',
    title: 'Montée de vitesse et rétrogradation',
    objectif: "L'élève coordonne accélération, changement de rapport et rétrogradation sans perdre sa trajectoire.",
    pourquoi: "Comprendre la relation vitesse → rapport → régime moteur → trajectoire pour accélérer et ralentir sans perdre la maîtrise.",
    comment: ['Anticiper le passage de rapport', 'Coordonner embrayage / accélération', 'Adapter le régime moteur à l\u2019allure'],
    exercices: [
      { nom: 'Séquence 1→2→3→2→1', niveau: 2, description: 'Sur ligne droite : 1 → 2 → 3 → ralentissement → 2 → 1 → arrêt.' },
      { nom: 'Intégration virage', niveau: 4, description: 'Insérer un virage entre les passages de rapport.' },
    ],
    observation: ['Regard qui plonge sur le compteur', 'Rétrogradation trop tôt', "Relâché d'embrayage brutal", 'Perte de trajectoire pendant le changement de rapport'],
    risques: ['Rupture de motricité', 'Sur-régime', 'Perte d\u2019équilibre à basse vitesse'],
    influences: ['Type de moto', 'Déclivité', 'Charge'],
    erreurs: ['Regarder le compteur', 'Vouloir rétrograder sans avoir ralenti', 'Enchaîner deux rapports sans stabiliser'],
    remediation: ['Faire les passages moto à l\u2019arrêt d\u2019abord (à vide)', 'Ligne droite avec repères visuels au sol', 'Décomposer accélération et rétrogradation'],
    questions: ["«Où doit être ton regard pendant que tu changes de rapport ?»", "«Comment ta moto te dit qu'elle est prête à monter d'un rapport ?»"],
    critere: "L'élève enchaîne montée et rétrogradation sans regarder le compteur, sans à-coup et sans perte de trajectoire.",
  },
  {
    id: 'arret-depart-tournant',
    title: 'Arrêt et départ en tournant',
    objectif: "L'élève associe arrêt stable, regard vers la sortie et redémarrage roue braquée.",
    pourquoi: "Compétence indispensable en circulation urbaine (feu, priorité, virage serré). Elle assemble équilibre + regard + direction + reprise de motricité.",
    comment: ['Arrivée lente', 'Arrêt stable, pieds au sol', 'Regard porté vers la sortie', 'Départ avec embrayage progressif et frein arrière'],
    exercices: [
      { nom: 'Arrêt-départ en virage large', niveau: 2, description: 'Arrivée lente → arrêt → regard sortie → départ en virage.' },
      { nom: 'Augmenter le rayon de difficulté', niveau: 4, description: 'Réduire progressivement le rayon disponible en sortie.' },
    ],
    observation: ['Regard qui décroche à l\u2019arrêt', 'Chute latérale au redémarrage', 'Sortie trop large', 'Frein arrière absent'],
    risques: ['Chute au redémarrage', 'Trajectoire coupée'],
    influences: ['Inclinaison du sol', 'Braquage disponible', 'Charge'],
    erreurs: ['S\u2019arrêter braquage droit puis vouloir tourner', "Ouvrir les gaz avant que l'équilibre ne soit trouvé"],
    remediation: ['Reprendre le patinage + frein arrière', 'Isoler le regard vers la sortie moto arrêtée'],
    questions: ["«Où regardes-tu au moment où tu t'arrêtes ?»", "«Qu'est-ce qui te permet de repartir sans poser le pied ?»"],
    critere: "L'élève effectue 3 arrêts-départs en virage successifs sans poser le pied, avec un regard clairement porté vers la sortie.",
  },
  {
    id: 'demi-tour',
    title: 'Demi-tour à basse vitesse',
    objectif: "L'élève réalise un demi-tour maîtrisé combinant préparation (freinage), prise d'angle (débrayage), regard, inertie et remise progressive de motricité.",
    pourquoi: 'Manœuvre-clé du plateau et de l\u2019usage urbain quotidien. Elle combine tous les fondamentaux : regard, embrayage, freinage, inertie, remise de motricité.',
    comment: ['Phase 1 — Préparation : allure adaptée + frein avant/arrière', 'Phase 2 — Prise d\u2019angle : regard sortie + débrayage complet + inertie', 'Phase 3 — Sortie : ré-embrayage progressif + gaz progressif pour retrouver la motricité'],
    exercices: [
      { nom: 'Demi-tour large', niveau: 1, description: 'Espace généreux, sans contrainte de temps.' },
      { nom: 'Demi-tour plus serré', niveau: 3, description: 'Espace réduit progressivement.' },
      { nom: 'Gauche puis droite', niveau: 3, description: 'Alternance des deux côtés pour identifier la dominance.' },
      { nom: 'Alternance gauche/droite', niveau: 4, description: 'Enchaînement de demi-tours alternés.' },
      { nom: 'Demi-tour dans le parcours', niveau: 5, description: 'Intégration dans un enchaînement de compétences.' },
    ],
    observation: ['Arrivée trop rapide', 'Regard sur le cône', 'Embrayage relâché trop tôt', 'Remise de gaz brutale', 'Crispation du guidon'],
    risques: ['Chute intérieure', 'Sortie trop large', 'Perte de motricité en sortie'],
    influences: ['Côté de réalisation (dominance)', 'Type de moto', 'Pente', 'Charge'],
    erreurs: ['Freiner pendant la prise d\u2019angle au lieu d\u2019avant', 'Vouloir accélérer trop tôt en sortie', 'Fixer le point le plus proche'],
    remediation: ['Reprendre la préparation moto à l\u2019arrêt', 'Refaire un demi-tour très large avec regard forcé vers la sortie', 'Isoler la remise de motricité en ligne droite'],
    questions: ["«À quel moment as-tu besoin de retrouver la motricité ?»", "«Qu'est-ce qui change entre ton demi-tour à gauche et ton demi-tour à droite ?»"],
    critere: "L'élève réalise 3 demi-tours consécutifs (gauche et droite) sans poser le pied, avec une sortie maîtrisée.",
    aRetenir: 'Je freine pour préparer → je débraye pour laisser vivre la moto → je regarde la sortie → je ré-embraye progressivement pour repartir.',
  },
];

/* ================================================================== */
/*  P2 — COORDONNER ET GAGNER EN PRÉCISION                             */
/* ================================================================== */
const P2_EXERCICES = [
  {
    id: 'lent-14',
    title: 'Lent — objectif 14 secondes',
    objectif: "L'élève construit une allure lente maîtrisée et reproductible, et obtient le temps comme une conséquence.",
    pourquoi: "Le chronomètre intervient progressivement. L'objectif est d'abord de construire une trajectoire stable ; le temps est la conséquence de la maîtrise, pas son point de départ.",
    comment: ['Trajectoire fluide', 'Regard loin', 'Zone de patinage', 'Frein arrière dosé en continu', 'Régulation à la sortie'],
    exercices: [
      { nom: 'Passage libre sans chrono', niveau: 2, description: 'Construire la trajectoire sans pression de temps.' },
      { nom: 'Chronométrage', niveau: 3, description: 'Introduire le temps comme indicateur.' },
      { nom: 'Recherche de régularité', niveau: 4, description: 'Reproduire le même temps sur plusieurs passages.' },
      { nom: 'Variation volontaire de l\u2019allure', niveau: 5, description: 'L\u2019élève choisit d\u2019accélérer ou de ralentir pendant le passage.' },
    ],
    observation: ['Chercher le temps avant la trajectoire', 'Regard trop proche', 'Calée', 'Perte d\u2019équilibre en sortie'],
    risques: ['Calée', 'Chute intérieure', 'Trajectoire imprécise sous le stress du chrono'],
    influences: ['Stress', 'Adhérence', 'Vent', 'Poids passager'],
    erreurs: ['Vouloir faire 14 s artificiellement', 'Rechercher la lenteur en fermant les gaz'],
    remediation: ['Retour au patinage + frein arrière ligne droite', 'Refaire sans chrono, en cherchant la régularité', 'Décomposer entrée / milieu / sortie'],
    questions: ["«Qu'est-ce qui t'a fait accélérer ?»", "«Où as-tu perdu ton équilibre ?»", "«Qu'a fait ton regard ?»"],
    critere: "L'élève reproduit le passage avec des temps réguliers (\u00b1 1 s) sans poser le pied, avec un frein arrière dosé en continu.",
    aRetenir: 'Je ne cherche pas à faire 14 s : je construis une allure maîtrisée qui me permet d\u2019obtenir le temps.',
  },
  {
    id: 'demi-tour-arrete',
    title: 'Demi-tour arrêté à gauche et à droite',
    objectif: "L'élève réalise un demi-tour arrêté maîtrisé des deux côtés en travaillant simultanément regard, équilibre, direction, allure, embrayage et coordination.",
    pourquoi: "Travailler simultanément l'ensemble des fondamentaux et mettre en lumière la dominance latérale.",
    comment: ['Préparer avec le freinage', 'Débrayer à la prise d\u2019angle', 'Regard vers la sortie', 'Ré-embrayer progressivement'],
    exercices: [
      { nom: 'Demi-tour large', niveau: 2, description: 'Espace généreux, sans contrainte.' },
      { nom: 'Demi-tour normal', niveau: 3, description: 'Espace standard d\u2019examen.' },
      { nom: 'Demi-tour plus contraint', niveau: 4, description: 'Espace réduit.' },
      { nom: 'Alternance gauche / droite', niveau: 4, description: 'Identifier et travailler la dominance.' },
      { nom: 'Arrêt → demi-tour → sortie contrôlée', niveau: 5, description: 'Ajout d\u2019un arrêt en amont et d\u2019une sortie précise.' },
    ],
    observation: ['Regard qui chute', 'Bras crispés', 'Chute intérieure', 'Sortie trop large', 'Différence marquée gauche/droite'],
    risques: ['Chute intérieure', 'Sortie trop large'],
    influences: ['Côté de réalisation (dominance)', 'Type de moto', 'Pente', 'Charge'],
    erreurs: ['Chercher à copier son côté fort sur le côté faible sans travail spécifique'],
    remediation: ['Reprendre l\u2019exercice côté faible avec un espace plus large', 'Isoler le regard moto à l\u2019arrêt', 'Refaire le patinage + frein arrière'],
    questions: ["«Qu'est-ce qui change entre ton demi-tour à gauche et ton demi-tour à droite ?»"],
    critere: "L'élève réalise 3 demi-tours consécutifs de chaque côté sans poser le pied ni élargir sa trajectoire.",
  },
  {
    id: 'demi-tour-lance',
    title: 'Demi-tour lancé',
    objectif: "L'élève apprend à préparer la manœuvre avant d'y entrer : la difficulté se prépare avant d'être rencontrée.",
    pourquoi: 'Introduit une notion pédagogique essentielle : voir → préparer → entrer → tourner → sortir. Le regard doit avoir une étape d\u2019avance.',
    comment: ['Regard avancé', 'Trajectoire préparée avant l\u2019entrée', 'Vitesse d\u2019entrée maîtrisée', 'Débrayage à la prise d\u2019angle pour préserver l\u2019inertie'],
    exercices: [
      { nom: 'Entrée modérée → préparation → rotation → sortie', niveau: 3, description: 'Marquer chaque phase distinctement.' },
    ],
    observation: ['Trajectoire non anticipée', 'Entrée trop rapide', 'Regard fixe sur l\u2019angle', 'Freinage trop tardif'],
    risques: ['Entrée trop rapide', 'Trajectoire coupée', 'Chute intérieure'],
    influences: ['Vitesse', 'Regard', 'Adhérence', 'Charge'],
    erreurs: ['Freiner dans l\u2019angle plutôt qu\u2019avant', 'Ouvrir les gaz trop tôt en sortie'],
    remediation: ['Refaire à faible allure sans lancement', 'Décomposer préparation / rotation / sortie', 'Placer un repère visuel de préparation en amont'],
    questions: ["«À quel moment as-tu commencé à préparer ton demi-tour ?»"],
    critere: "L'élève entre à allure adaptée, réalise la rotation sans poser le pied et ressort dans l\u2019axe attendu.",
    aRetenir: 'La difficulté se prépare avant d\u2019être rencontrée.',
  },
  {
    id: 'repartition-freinage',
    title: 'Répartition du freinage',
    objectif: "L'élève comprend le rôle complémentaire des deux freins et dose leur pression selon la situation.",
    pourquoi: 'Le freinage doit être adapté à la situation, à la vitesse et à l\u2019adhérence. Cela prépare le freinage d\u2019urgence de P3.',
    comment: ['Frein avant : puissance principale', 'Frein arrière : stabilisation et complément', 'Pression progressive', 'Regard maintenu loin'],
    exercices: [
      { nom: 'Frein arrière seul', niveau: 1, description: 'Faible allure. Ressentir la stabilisation apportée.' },
      { nom: 'Frein avant seul', niveau: 2, description: 'Environnement sécurisé, allure adaptée. Ressentir la puissance.' },
      { nom: 'Association des 2 freins', niveau: 3, description: 'Coordonner pression progressive sur les deux.' },
      { nom: 'Freinage progressif avec arrêt précis', niveau: 4, description: 'Immobilisation à un repère.' },
    ],
    observation: ['Blocage roue', 'Freinage brutal', 'Déséquilibre', 'Utilisation d\u2019un seul frein'],
    risques: ['Blocage de roue', 'Perte d\u2019équilibre à l\u2019arrêt'],
    influences: ['Adhérence', 'Charge', 'Type de moto', 'Pneus', 'Température'],
    erreurs: ['Serrer le frein avant d\u2019un coup', 'Utiliser uniquement l\u2019arrière par prudence'],
    remediation: ['Isoler chaque frein sur ligne droite courte', 'Progression de la pression sur 5 paliers'],
    questions: ["«Quel frein t\u2019apporte le plus de puissance ?»", "«Quel frein t\u2019apporte le plus de stabilité ?»"],
    critere: "L'élève immobilise la moto à un repère précis en utilisant les 2 freins de manière progressive et coordonnée.",
  },
];

/* ================================================================== */
/*  P3 — MAÎTRISER ET ENCHAÎNER                                        */
/* ================================================================== */
const P3_EXERCICES = [
  {
    id: 'contre-braquage-slalom',
    title: 'Contre-braquage et slalom à 40 km/h',
    objectif: "L'élève comprend et met en œuvre le contre-braquage comme une coordination globale du corps sur la moto.",
    pourquoi: 'La moto se dirige principalement par contre-braquage à partir d\u2019une certaine vitesse. Le contre-braquage n\u2019est pas un simple geste sur le guidon, c\u2019est une coordination globale.',
    comment: ['Regard vers la sortie', 'Position stable et détendue', 'Genoux au contact', 'Bras souples', 'Action progressive sur le guidon', 'Appui repose-pieds + jambes + bassin', 'Vitesse adaptée'],
    exercices: [
      { nom: 'Contre-braquage à vitesse modérée', niveau: 2, description: 'Ressentir l\u2019effet d\u2019une action guidon.' },
      { nom: 'Changement de direction gauche/droite', niveau: 3, description: 'Enchaîner deux inclinaisons opposées.' },
      { nom: 'Slalom', niveau: 4, description: 'Enchaînement continu.' },
      { nom: 'Slalom à vitesse progressivement adaptée', niveau: 5, description: 'Augmenter progressivement l\u2019allure.' },
    ],
    observation: ['Fixation des cônes', 'Crispation guidon', 'Entrée trop rapide', 'Trajectoire large', 'Freinage pendant le slalom'],
    risques: ['Trajectoire large en sortie', 'Perte de contrôle sur cône touché'],
    influences: ['Stress', 'Appréhension de la vitesse', 'Puissance et poids moto', 'Pneus', 'Revêtement', 'Vent', 'Température'],
    erreurs: ['Résumer le contre-braquage à «pousser le guidon»', 'Se crisper sur le guidon', 'Regarder les cônes'],
    remediation: ['Reprendre à allure modérée avec espacement plus large', 'Isoler le rôle du regard puis des jambes', 'Faire ressentir l\u2019appui repose-pieds moto à l\u2019arrêt'],
    questions: ["«Où regardes-tu lorsque tu veux que la moto change de direction ?»", "«Quel rôle jouent tes jambes ?»"],
    critere: "L'élève réalise le slalom à l\u2019allure attendue sans faire tomber de cône, avec un regard clairement porté vers la sortie.",
    aRetenir: 'La maîtrise de la trajectoire vient du corps entier, pas seulement des mains.',
  },
  {
    id: 'freinage-urgence',
    title: 'Freinage d\u2019urgence',
    objectif: "L'élève réagit face à un danger imprévu en conservant la maîtrise de sa moto.",
    pourquoi: "L'urgence ne doit pas créer de précipitation. Elle doit déclencher une séquence maîtrisée : voir → redresser → freiner → contrôler.",
    comment: ['Identifier le point de déclenchement', 'Garder le regard loin', 'Redresser la moto', 'Fermer les gaz', 'Utiliser les 2 freins', 'Augmenter progressivement la pression', 'Maintenir la trajectoire', 'Immobiliser la moto'],
    exercices: [
      { nom: 'Comprendre → pratiquer', niveau: 1, description: 'Explication + démonstration + premier essai à faible allure.' },
      { nom: 'Séquence complète', niveau: 3, description: 'Accélération → repère → fermeture des gaz → freinage → arrêt.' },
      { nom: 'Variation du point de déclenchement', niveau: 4, description: 'Modifier la distance ou la vitesse.' },
      { nom: 'Variation des conditions', niveau: 5, description: 'Adhérence différente, pente, présence d\u2019un enchaînement.' },
    ],
    observation: ['Freinage trop tardif', 'Freinage brutal', 'Blocage d\u2019une roue', 'Regard sur l\u2019obstacle', 'Moto pas assez redressée', 'Mauvaise coordination avant/arrière'],
    risques: ['Blocage roue', 'Chute par déséquilibre', 'Choc arrière'],
    influences: ['Vitesse', 'Pneus', 'Adhérence', 'Revêtement', 'Temps de réaction', 'Stress', 'Charge'],
    erreurs: ['Serrer le frein avant d\u2019un coup', 'Freiner sans redresser la moto', 'Regarder l\u2019obstacle'],
    remediation: ['Reprendre la répartition du freinage sur ligne droite courte', 'Isoler la fermeture des gaz avant le freinage'],
    questions: ["«Quand as-tu identifié le danger ?»", "«Quand as-tu commencé à freiner ?»", "«Où regardais-tu ?»"],
    critere: "L'élève immobilise la moto dans la distance attendue, sans blocage de roue, avec regard maintenu loin.",
    aRetenir: 'Je vois, je redresse, je freine, je contrôle.',
  },
  {
    id: 'evitement',
    title: 'Évitement',
    objectif: "L'élève identifie qu'une situation dangereuse peut nécessiter d'éviter, de freiner, ou de combiner les deux.",
    pourquoi: 'Freiner n\u2019est pas toujours la bonne réponse. L\u2019évitement introduit une autre solution face à un danger.',
    comment: ['Identifier l\u2019obstacle', 'Adapter la vitesse en amont', 'Regard vers la zone de sortie', 'Déclencher le contre-braquage', 'Maintenir la trajectoire d\u2019évitement', 'Revenir progressivement dans l\u2019axe', 'Freiner seulement après si la situation le permet'],
    exercices: [
      { nom: 'Approche → obstacle → évitement → retour', niveau: 3, description: 'Trajectoire simple.' },
      { nom: 'Évitement à gauche puis à droite', niveau: 4, description: 'Alterner les deux côtés.' },
      { nom: 'Vitesse progressivement adaptée', niveau: 5, description: 'Augmenter l\u2019allure d\u2019approche.' },
      { nom: 'Enchaînement avec freinage', niveau: 5, description: 'Éviter puis freiner.' },
    ],
    observation: ['Fixation de l\u2019obstacle', 'Freinage pendant l\u2019évitement', 'Mouvement brutal du guidon', 'Mauvaise anticipation', 'Vitesse excessive'],
    risques: ['Perte de trajectoire', 'Chute par mouvement brutal'],
    influences: ['Vitesse', 'Distance disponible', 'Temps de réaction', 'Regard', 'Adhérence', 'État émotionnel'],
    erreurs: ['Vouloir freiner et éviter en même temps', 'Fixer l\u2019obstacle des yeux'],
    remediation: ['Reprendre à allure faible sans freinage associé', 'Décomposer regard → contre-braquage → retour axe'],
    questions: ["«Si tu regardes l'obstacle, où risques-tu d'aller ?»"],
    critere: "L'élève évite l\u2019obstacle sans le toucher, en gardant sa trajectoire de sortie sous contrôle.",
    aRetenir: 'Je ne regarde pas l\u2019obstacle que je veux éviter. Je regarde où je veux aller.',
  },
  {
    id: 'demi-tour-passager',
    title: 'Demi-tour avec passager',
    objectif: "L'élève adapte sa conduite au comportement modifié de la moto avec passager et communique avec lui.",
    pourquoi: 'Le passager modifie le comportement de la moto : poids, équilibre, inertie, réactions. L\u2019élève ne conduit plus exactement la même machine.',
    comment: ['Avant le départ : expliquer comment monter, définir la position, expliquer comment se tenir, établir un code de communication', 'Démarrage progressif', 'Regard vers la sortie', 'Gestion de l\u2019embrayage', 'Frein arrière adapté', 'Trajectoire souple', 'Arrêt progressif'],
    exercices: [
      { nom: 'Demi-tour large', niveau: 2, description: 'Se familiariser avec le nouveau comportement.' },
      { nom: 'Allure réduite → demi-tour → sortie', niveau: 4, description: 'Introduire la difficulté progressivement.' },
    ],
    observation: ['Déstabilisation au démarrage', 'Passager qui accompagne mal', 'Arrêt brutal', 'Regard trop proche', 'Mauvaise communication'],
    risques: ['Chute au démarrage', 'Trajectoire élargie'],
    influences: ['Poids du passager', 'Taille', 'Répartition des masses', 'Appréhension', 'Comportement du passager'],
    erreurs: ['Traiter le passager comme un objet inerte', 'Sous-estimer l\u2019inertie ajoutée'],
    remediation: ['Refaire la préparation moto à l\u2019arrêt', 'Reprendre le demi-tour large seul avant de réintégrer le passager'],
    questions: ["«Qu'as-tu ressenti par rapport au même demi-tour sans passager ?»", "«Comment votre communication a-t-elle fonctionné ?»"],
    critere: "L'élève réalise le demi-tour avec passager sans poser le pied, avec un démarrage et un arrêt progressifs.",
  },
  {
    id: 'assemblage',
    title: 'Assemblage du parcours',
    objectif: "L'élève passe d'une compétence à l'autre sans perdre la maîtrise, dans une logique : information → décision → action → contrôle → adaptation.",
    pourquoi: 'C\u2019est le vrai changement de P3 : passer de la réalisation d\u2019exercices isolés à la maîtrise d\u2019une situation complète.',
    comment: ['Allure réduite', 'Demi-tour', 'Accélération', 'Slalom', 'Freinage', 'Demi-tour / repositionnement', 'Évitement', 'Arrêt'],
    exercices: [
      { nom: 'Exercices enchaînés sans recherche de performance', niveau: 3, description: 'Simple mise bout à bout.' },
      { nom: 'Parcours complet', niveau: 4, description: 'Enchaînement fluide.' },
      { nom: 'Parcours avec observation des erreurs', niveau: 5, description: 'Le débriefing devient central.' },
      { nom: 'Examen blanc', niveau: 6, description: 'Conditions proches de l\u2019évaluation.' },
    ],
    observation: ['Perdre ses repères entre 2 exercices', 'Anticipation insuffisante', 'Regard qui décroche', 'Baisse de vigilance en fin de parcours'],
    risques: ['Erreur d\u2019anticipation cumulée', 'Chute par transfert de charge inattendu'],
    influences: ['Fatigue', 'Stress', 'Enchaînement', 'Vitesse'],
    erreurs: ['Vouloir aller plus vite pour «gagner du temps»', 'Ignorer une petite erreur au lieu de l\u2019analyser'],
    remediation: ['Refaire isolément la transition problématique', 'Reprendre le débriefing par question ouverte'],
    questions: ["«Est-ce que tu sais reproduire ta réussite lorsque plusieurs compétences s'enchaînent ?»", "«Qu'est-ce qui a fonctionné ? Où as-tu rencontré une difficulté ?»", "«Que vas-tu changer au prochain passage ?»"],
    critere: "L'élève enchaîne toutes les compétences du parcours en conservant regard, trajectoire et maîtrise, avec une auto-évaluation lucide.",
    debrief: [
      "«Qu'est-ce qui a fonctionné ?»",
      "«Où as-tu rencontré une difficulté ?»",
      '«Pourquoi ?»',
      '«Quel risque as-tu identifié ?»',
      '«Quelle influence a modifié ta conduite ?»',
      "«Que vas-tu changer au prochain passage ?»",
    ],
  },
];

export const PHASES = [
  {
    id: 'p1',
    code: 'P1',
    title: 'Découvrir et ressentir',
    subtitle: 'Première prise en main de la moto',
    color: '#22c55e',
    context: 'plateau',
    accroche: "L'élève découvre sa moto et comprend progressivement comment ses commandes influencent son comportement. On construit les premiers automatismes sans rechercher la performance.",
    exercises: P1_EXERCICES,
  },
  {
    id: 'p2',
    code: 'P2',
    title: 'Coordonner et gagner en précision',
    subtitle: '«Je réussis un exercice» → «Je comprends ce qui me permet de le réussir»',
    color: '#f59e0b',
    context: 'plateau',
    accroche: "Lorsque les premières sensations sont acquises, l'élève commence à coordonner ses différentes actions. Le temps devient une conséquence de la maîtrise, pas son point de départ.",
    exercises: P2_EXERCICES,
  },
  {
    id: 'p3',
    code: 'P3',
    title: 'Maîtriser et enchaîner',
    subtitle: 'Maîtrise à allure élevée et assemblage',
    color: '#e11d48',
    context: 'plateau',
    accroche: "On passe de la réalisation d'exercices isolés à la maîtrise d'une situation complète : information → décision → action → contrôle → adaptation.",
    exercises: P3_EXERCICES,
  },
];

/**
 * Version SYNTHÉTISÉE (markdown) copiable pour le livret d'apprentissage.
 */
export function buildSynthese() {
  const lines = [];
  lines.push('# Progression pédagogique plateau moto A1/A2 — CODE 2 ROUES');
  lines.push('');
  lines.push(`**Méthode :** ${INTRO_METHODOLOGIE.baseline}`);
  lines.push('');
  lines.push('**Cinq questions permanentes :**');
  INTRO_METHODOLOGIE.questions.forEach(q => lines.push(`- **${q.label}** ${q.text}`));
  lines.push('');
  lines.push(`> ${INTRO_METHODOLOGIE.regleDuree}`);
  lines.push('');
  PHASES.forEach(phase => {
    lines.push(`## ${phase.code} — ${phase.title.toUpperCase()}`);
    lines.push(`*${phase.subtitle}*`);
    lines.push('');
    lines.push(phase.accroche);
    lines.push('');
    phase.exercises.forEach(ex => {
      lines.push(`### ${ex.title}`);
      lines.push(`**🎯 Objectif :** ${ex.objectif}`);
      lines.push(`**Pourquoi ?** ${ex.pourquoi}`);
      lines.push('**Comment ?**');
      ex.comment.forEach(c => lines.push(`- ${c}`));
      if (ex.exercices?.length) {
        lines.push('**🏍️ Exercices :**');
        ex.exercices.forEach(e => lines.push(`- **${e.nom}** _(niv. ${e.niveau})_ : ${e.description}`));
      }
      if (ex.observation?.length) lines.push('**👀 Ce que l’enseignant observe :** ' + ex.observation.join(' · '));
      lines.push('**⚠️ Risques :** ' + ex.risques.join(' · '));
      lines.push('**🌦️ Influences :** ' + ex.influences.join(' · '));
      if (ex.erreurs?.length) lines.push('**Erreurs fréquentes :** ' + ex.erreurs.join(' · '));
      if (ex.remediation?.length) lines.push('**🔧 Remédiation :** ' + ex.remediation.join(' · '));
      if (ex.questions?.length) {
        lines.push('**🗣️ Questions pédagogiques :**');
        ex.questions.forEach(q => lines.push(`- ${q}`));
      }
      if (ex.critere) lines.push(`**✅ Critère de réussite :** ${ex.critere}`);
      if (ex.aRetenir) lines.push(`> **À retenir :** ${ex.aRetenir}`);
      lines.push('');
    });
  });
  lines.push('## Le demi-tour à basse vitesse');
  lines.push(DEMI_TOUR.intro);
  lines.push('');
  DEMI_TOUR.steps.forEach(s => {
    lines.push(`### ${s.n}. ${s.title}`);
    s.bullets.forEach(b => lines.push(`- ${b}`));
    lines.push('');
  });
  lines.push(`> **À retenir :** ${DEMI_TOUR.aRetenir}`);
  lines.push('');
  lines.push(`*${DEMI_TOUR.disclaimer}*`);
  return lines.join('\n');
}
